import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { api } from "../api";
import { readableError } from "../composables/format";
import { useAuthStore } from "./auth";
import { useToast } from "../composables/useToast";
import type { Channel, ChannelMember, ChannelRole } from "../types";

const CURRENT_CHANNEL_ID_KEY = "mindback.currentChannelId";
const LEGACY_CURRENT_SLUG_KEY = "mindback.currentChannelSlug";

export const useChannelStore = defineStore("channels", () => {
  const toast = useToast();
  const channels = ref<Channel[]>([]);
  const currentChannelId = ref(localStorage.getItem(CURRENT_CHANNEL_ID_KEY) || localStorage.getItem(LEGACY_CURRENT_SLUG_KEY) || "inbox");
  const loading = ref(false);

  const currentChannel = computed(() => {
    return channels.value.find((item) => item.id === currentChannelId.value) || defaultChannel() || null;
  });
  const mutedChannelIds = computed(() => channels.value.filter((item) => item.muted).map((item) => item.id));

  async function loadChannels() {
    loading.value = true;
    try {
      const response = await api.channels();
      replaceChannels(response.channels);
    } finally {
      loading.value = false;
    }
  }

  function replaceChannels(nextChannels: Channel[]) {
    channels.value = nextChannels;
    if (!channels.value.some((item) => item.id === currentChannelId.value)) {
      currentChannelId.value = resolveChannelId(currentChannelId.value);
    }
    localStorage.setItem(CURRENT_CHANNEL_ID_KEY, currentChannelId.value);
    localStorage.removeItem(LEGACY_CURRENT_SLUG_KEY);
  }

  async function switchChannel(channelIdOrSlug: string) {
    currentChannelId.value = resolveChannelId(channelIdOrSlug);
    localStorage.setItem(CURRENT_CHANNEL_ID_KEY, currentChannelId.value);
    localStorage.removeItem(LEGACY_CURRENT_SLUG_KEY);
  }

  function resolveChannelId(channelIdOrSlug?: string | null) {
    if (channelIdOrSlug) {
      const byId = channels.value.find((item) => item.id === channelIdOrSlug);
      if (byId) return byId.id;

      const auth = useAuthStore();
      const ownBySlug = channels.value.find((item) => item.slug === channelIdOrSlug && item.owner_id === auth.user?.id);
      if (ownBySlug) return ownBySlug.id;

      const bySlug = channels.value.find((item) => item.slug === channelIdOrSlug);
      if (bySlug) return bySlug.id;
    }
    return defaultChannel()?.id || "inbox";
  }

  function resolveStartupChannelId() {
    const auth = useAuthStore();
    if (auth.user?.startup_channel_mode === "default" && auth.user.default_channel_id) {
      return resolveChannelId(auth.user.default_channel_id);
    }
    return resolveChannelId(localStorage.getItem(CURRENT_CHANNEL_ID_KEY) || localStorage.getItem(LEGACY_CURRENT_SLUG_KEY));
  }

  async function createChannel(name: string, slug?: string) {
    try {
      const response = await api.createChannel({ name, slug });
      await loadChannels();
      await switchChannel(response.channel.id);
      toast.success("频道已创建");
      return response.channel;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function updateChannel(id: string, payload: Partial<Channel>) {
    try {
      const response = await api.patchChannel(id, payload);
      await loadChannels();
      toast.success("频道设置已保存");
      return response.channel;
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function archiveChannel(id: string) {
    try {
      await api.deleteChannel(id);
      await loadChannels();
      toast.success("频道已归档");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function markRead(id: string) {
    await api.markChannelRead(id);
    await loadChannels();
  }

  async function mute(id: string, muted: boolean) {
    try {
      await api.muteChannel(id, muted);
      await loadChannels();
      toast.success(muted ? "频道已静音" : "频道已取消静音");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function members(id: string): Promise<ChannelMember[]> {
    const response = await api.channelMembers(id);
    return response.members;
  }

  async function invite(id: string, email: string, role: ChannelRole) {
    try {
      await api.inviteMember(id, { email, role });
      await loadChannels();
      toast.success("成员已邀请");
    } catch (error) {
      toast.error(readableError(error));
      throw error;
    }
  }

  async function updateMember(id: string, userId: string, role: ChannelRole) {
    await api.patchMember(id, userId, { role });
    await loadChannels();
  }

  async function removeMember(id: string, userId: string) {
    await api.removeMember(id, userId);
    await loadChannels();
  }

  function clearLocal() {
    channels.value = [];
    currentChannelId.value = "inbox";
    localStorage.removeItem(CURRENT_CHANNEL_ID_KEY);
    localStorage.removeItem(LEGACY_CURRENT_SLUG_KEY);
  }

  function defaultChannel() {
    const auth = useAuthStore();
    return channels.value.find((item) => item.slug === "inbox" && item.owner_id === auth.user?.id)
      || channels.value.find((item) => item.slug === "inbox")
      || channels.value[0];
  }

  return {
    channels,
    currentChannelId,
    currentChannel,
    mutedChannelIds,
    loading,
    loadChannels,
    replaceChannels,
    switchChannel,
    resolveChannelId,
    resolveStartupChannelId,
    createChannel,
    updateChannel,
    archiveChannel,
    markRead,
    mute,
    members,
    invite,
    updateMember,
    removeMember,
    clearLocal
  };
});
